import { PreviewDataset } from '../preview.types';

// Skyline Regional Airways — a 100% SYNTHETIC airline workforce for the "Try It Now" sandbox.
// Fictional company, fictional people. Shaped to look realistic (plausible aviation roles,
// a real-world competency framework, a believable spread of coverage, gaps, and expiries) but
// never derived from real customer data (ADR 0001 #4). Some employees intentionally miss a
// required competency (a gap) and some certifications carry expiry dates, so the later
// Risk/Compliance/TNA lenses have a story to tell over the same fixture.

export const aviationDataset: PreviewDataset = {
  code: 'aviation',
  companyName: 'Skyline Regional Airways',
  industry: 'Aviation',
  attributeDefinitions: [
    { id: 'a-type-a320', name: 'Type Rating — A320 Family', category: 'Technical' },
    { id: 'a-type-atr', name: 'Type Rating — ATR 72', category: 'Technical' },
    { id: 'a-crm', name: 'Crew Resource Management (CRM)', category: 'Safety' },
    { id: 'a-dg', name: 'Dangerous Goods Awareness', category: 'Compliance' },
    { id: 'a-avsec', name: 'Aviation Security (AVSEC)', category: 'Compliance' },
    { id: 'a-first-aid', name: 'Aviation First Aid', category: 'Safety' },
    { id: 'a-fire-smoke', name: 'Fire & Smoke Training', category: 'Safety' },
    { id: 'a-ramp-safety', name: 'Ramp Safety', category: 'Safety' },
    { id: 'a-fuelling', name: 'Aircraft Fuelling Procedures', category: 'Technical' },
    { id: 'a-sms', name: 'Safety Management System (SMS)', category: 'Compliance' },
    { id: 'a-human-factors', name: 'Human Factors', category: 'Safety' },
    { id: 'a-load-control', name: 'Load Control & Weight Balance', category: 'Technical' },
    { id: 'a-deicing', name: 'De-Icing Procedures', category: 'Technical' },
    { id: 'a-pax-service', name: 'Passenger Service', category: 'Operations' },
  ],
  // Each job splits its competencies into Mandatory (safety/compliance-mandated), Required (role
  // skills) and Desirable (nice-to-have). Mandatory + Required = the compliance set (a missing one
  // is a gap); Desirable is surfaced in the overview but never counts against compliance.
  jobs: [
    {
      id: 'j-captain',
      title: 'Captain',
      department: 'Flight Operations',
      description:
        'Pilot-in-command of Skyline Regional Airways aircraft, holding final authority for the safe, on-time and compliant operation of every assigned flight. Leads the flight crew through pre-flight planning, briefings and en-route decision-making, mentors First Officers, and upholds the airline’s safety management standards.',
      mandatoryAttributeIds: ['a-dg', 'a-first-aid', 'a-fire-smoke', 'a-sms'],
      requiredAttributeIds: ['a-type-a320', 'a-crm', 'a-human-factors'],
      desirableAttributeIds: ['a-pax-service'],
    },
    {
      id: 'j-first-officer',
      title: 'First Officer',
      department: 'Flight Operations',
      description:
        'Second-in-command on the flight deck, supporting the Captain across all phases of flight. Shares handling and monitoring duties, manages communications and navigation, and is trained to assume command if required while building the type currency needed to progress toward a command upgrade.',
      mandatoryAttributeIds: ['a-dg', 'a-first-aid', 'a-fire-smoke'],
      requiredAttributeIds: ['a-type-a320', 'a-crm', 'a-human-factors'],
      desirableAttributeIds: ['a-sms'],
    },
    {
      id: 'j-cabin-crew',
      title: 'Cabin Crew',
      department: 'Cabin Services',
      description:
        'Responsible for the safety, security and comfort of passengers in the cabin. Conducts pre-flight safety checks, leads emergency and first-aid response, manages boarding and in-flight service, and ensures aviation security and dangerous-goods requirements are met on every sector.',
      mandatoryAttributeIds: ['a-dg', 'a-first-aid', 'a-fire-smoke', 'a-avsec'],
      requiredAttributeIds: ['a-crm', 'a-pax-service'],
      desirableAttributeIds: ['a-human-factors'],
    },
    {
      id: 'j-line-engineer',
      title: 'Line Maintenance Engineer',
      department: 'Engineering & Maintenance',
      description:
        'Certifies the airworthiness of aircraft between flights. Performs scheduled line checks, troubleshoots and rectifies defects, oversees refuelling and ground servicing, and releases aircraft to service in line with maintenance and safety-management procedures.',
      mandatoryAttributeIds: ['a-dg', 'a-sms'],
      requiredAttributeIds: ['a-human-factors', 'a-fuelling', 'a-load-control'],
      desirableAttributeIds: ['a-ramp-safety'],
    },
    {
      id: 'j-ground-ops',
      title: 'Ground Operations Agent',
      department: 'Ground Operations',
      description:
        'Keeps aircraft turnarounds safe and on schedule on the ramp. Handles loading and unloading, aircraft marshalling, de-icing and refuelling support, and ensures ramp-safety, security and dangerous-goods rules are followed throughout every turnaround.',
      mandatoryAttributeIds: ['a-dg', 'a-avsec'],
      requiredAttributeIds: ['a-ramp-safety', 'a-fuelling', 'a-deicing'],
      desirableAttributeIds: ['a-first-aid'],
    },
    {
      id: 'j-dispatcher',
      title: 'Flight Dispatcher',
      department: 'Operations Control',
      description:
        'Plans and releases flights from the operations centre. Prepares operational flight plans, calculates load and balance, monitors weather and NOTAMs, and coordinates with crews and air traffic services so each flight departs legal, safe and efficient.',
      mandatoryAttributeIds: ['a-dg', 'a-sms', 'a-avsec'],
      requiredAttributeIds: ['a-load-control'],
      desirableAttributeIds: ['a-human-factors'],
    },
    {
      id: 'j-safety-officer',
      title: 'Safety & Compliance Officer',
      department: 'Safety & Compliance',
      description:
        'Owns the airline’s safety management system and regulatory compliance. Runs hazard reporting and risk assessments, audits operational areas, investigates occurrences, and drives a just safety culture across the flight, cabin, engineering and ground teams.',
      mandatoryAttributeIds: ['a-sms', 'a-dg', 'a-avsec'],
      requiredAttributeIds: ['a-human-factors'],
      desirableAttributeIds: ['a-crm'],
    },
  ],
  employees: [
    {
      id: 'e01',
      firstName: 'Sarah',
      lastName: 'Whitfield',
      jobId: 'j-captain',
      location: 'Auckland Hub',
      attributes: [
        { attributeDefinitionId: 'a-type-a320', proficiency: 'expert' },
        { attributeDefinitionId: 'a-crm', proficiency: 'competent', expiresOn: '2026-11-30' },
        { attributeDefinitionId: 'a-dg', proficiency: 'competent', expiresOn: '2027-02-01' },
        { attributeDefinitionId: 'a-first-aid', proficiency: 'competent', expiresOn: '2026-09-15' },
        { attributeDefinitionId: 'a-fire-smoke', proficiency: 'competent' },
        { attributeDefinitionId: 'a-human-factors', proficiency: 'competent' },
        { attributeDefinitionId: 'a-sms', proficiency: 'developing' },
        { attributeDefinitionId: 'a-pax-service', proficiency: 'competent' }, // desirable — held
      ],
    },
    {
      id: 'e02',
      firstName: 'David',
      lastName: 'Nguyen',
      jobId: 'j-captain',
      location: 'Wellington Base',
      attributes: [
        { attributeDefinitionId: 'a-type-a320', proficiency: 'expert' },
        { attributeDefinitionId: 'a-crm', proficiency: 'competent' },
        { attributeDefinitionId: 'a-dg', proficiency: 'competent', expiresOn: '2026-01-10' },
        { attributeDefinitionId: 'a-first-aid', proficiency: 'competent' },
        { attributeDefinitionId: 'a-fire-smoke', proficiency: 'competent' },
        { attributeDefinitionId: 'a-human-factors', proficiency: 'competent' },
        // missing a-sms → gap
      ],
    },
    {
      id: 'e03',
      firstName: 'Olivia',
      lastName: 'Bennett',
      jobId: 'j-first-officer',
      location: 'Auckland Hub',
      attributes: [
        { attributeDefinitionId: 'a-type-a320', proficiency: 'competent' },
        { attributeDefinitionId: 'a-crm', proficiency: 'competent' },
        { attributeDefinitionId: 'a-dg', proficiency: 'competent', expiresOn: '2027-04-20' },
        { attributeDefinitionId: 'a-first-aid', proficiency: 'competent' },
        { attributeDefinitionId: 'a-fire-smoke', proficiency: 'competent' },
        { attributeDefinitionId: 'a-human-factors', proficiency: 'developing' },
        { attributeDefinitionId: 'a-sms', proficiency: 'developing' }, // desirable — held
      ],
    },
    {
      id: 'e04',
      firstName: 'Marcus',
      lastName: 'Lee',
      jobId: 'j-first-officer',
      location: 'Christchurch Base',
      attributes: [
        { attributeDefinitionId: 'a-type-a320', proficiency: 'developing' },
        { attributeDefinitionId: 'a-crm', proficiency: 'developing' },
        { attributeDefinitionId: 'a-dg', proficiency: 'competent' },
        { attributeDefinitionId: 'a-first-aid', proficiency: 'competent' },
        // missing a-fire-smoke, a-human-factors → gaps
      ],
    },
    {
      id: 'e05',
      firstName: 'Emily',
      lastName: 'Carter',
      jobId: 'j-cabin-crew',
      location: 'Auckland Hub',
      attributes: [
        { attributeDefinitionId: 'a-crm', proficiency: 'competent' },
        { attributeDefinitionId: 'a-dg', proficiency: 'competent', expiresOn: '2026-10-05' },
        { attributeDefinitionId: 'a-first-aid', proficiency: 'competent' },
        { attributeDefinitionId: 'a-fire-smoke', proficiency: 'competent' },
        { attributeDefinitionId: 'a-avsec', proficiency: 'competent' },
        { attributeDefinitionId: 'a-pax-service', proficiency: 'expert' },
        { attributeDefinitionId: 'a-human-factors', proficiency: 'competent' }, // desirable — held
      ],
    },
    {
      id: 'e06',
      firstName: 'Liam',
      lastName: "O'Connor",
      jobId: 'j-cabin-crew',
      location: 'Wellington Base',
      attributes: [
        { attributeDefinitionId: 'a-crm', proficiency: 'competent' },
        { attributeDefinitionId: 'a-dg', proficiency: 'competent', expiresOn: '2026-01-20' },
        { attributeDefinitionId: 'a-first-aid', proficiency: 'competent' },
        { attributeDefinitionId: 'a-avsec', proficiency: 'competent' },
        { attributeDefinitionId: 'a-pax-service', proficiency: 'competent' },
        // missing a-fire-smoke → gap
      ],
    },
    {
      id: 'e07',
      firstName: 'Aroha',
      lastName: 'Walker',
      jobId: 'j-cabin-crew',
      location: 'Christchurch Base',
      attributes: [
        { attributeDefinitionId: 'a-crm', proficiency: 'competent' },
        { attributeDefinitionId: 'a-dg', proficiency: 'competent' },
        { attributeDefinitionId: 'a-first-aid', proficiency: 'competent' },
        { attributeDefinitionId: 'a-fire-smoke', proficiency: 'competent' },
        { attributeDefinitionId: 'a-pax-service', proficiency: 'expert' },
        // missing a-avsec → gap
      ],
    },
    {
      id: 'e08',
      firstName: 'Tom',
      lastName: 'Fielding',
      jobId: 'j-line-engineer',
      location: 'Auckland Hub',
      attributes: [
        { attributeDefinitionId: 'a-dg', proficiency: 'competent', expiresOn: '2027-01-31' },
        { attributeDefinitionId: 'a-sms', proficiency: 'competent' },
        { attributeDefinitionId: 'a-human-factors', proficiency: 'expert' },
        { attributeDefinitionId: 'a-fuelling', proficiency: 'competent' },
        { attributeDefinitionId: 'a-load-control', proficiency: 'competent' },
        { attributeDefinitionId: 'a-ramp-safety', proficiency: 'competent' }, // desirable — held
      ],
    },
    {
      id: 'e09',
      firstName: 'Priya',
      lastName: 'Sharma',
      jobId: 'j-line-engineer',
      location: 'Wellington Base',
      attributes: [
        { attributeDefinitionId: 'a-dg', proficiency: 'competent' },
        { attributeDefinitionId: 'a-sms', proficiency: 'competent' },
        { attributeDefinitionId: 'a-human-factors', proficiency: 'competent' },
        { attributeDefinitionId: 'a-fuelling', proficiency: 'developing' },
        // missing a-load-control → gap
      ],
    },
    {
      id: 'e10',
      firstName: 'Jack',
      lastName: 'Thompson',
      jobId: 'j-ground-ops',
      location: 'Auckland Hub',
      attributes: [
        { attributeDefinitionId: 'a-ramp-safety', proficiency: 'competent' },
        { attributeDefinitionId: 'a-dg', proficiency: 'competent', expiresOn: '2026-08-12' },
        { attributeDefinitionId: 'a-avsec', proficiency: 'competent' },
        { attributeDefinitionId: 'a-fuelling', proficiency: 'expert' },
        { attributeDefinitionId: 'a-deicing', proficiency: 'competent' },
        { attributeDefinitionId: 'a-first-aid', proficiency: 'competent' }, // desirable — held
      ],
    },
    {
      id: 'e11',
      firstName: 'Sofia',
      lastName: 'Russo',
      jobId: 'j-ground-ops',
      location: 'Christchurch Base',
      attributes: [
        { attributeDefinitionId: 'a-ramp-safety', proficiency: 'competent' },
        { attributeDefinitionId: 'a-dg', proficiency: 'developing' },
        { attributeDefinitionId: 'a-fuelling', proficiency: 'competent' },
        // missing a-avsec, a-deicing → gaps
      ],
    },
    {
      id: 'e12',
      firstName: 'Hannah',
      lastName: 'Mills',
      jobId: 'j-dispatcher',
      location: 'Auckland Hub',
      attributes: [
        { attributeDefinitionId: 'a-load-control', proficiency: 'expert' },
        { attributeDefinitionId: 'a-dg', proficiency: 'competent', expiresOn: '2027-03-01' },
        { attributeDefinitionId: 'a-sms', proficiency: 'competent' },
        { attributeDefinitionId: 'a-avsec', proficiency: 'competent' },
        { attributeDefinitionId: 'a-human-factors', proficiency: 'competent' }, // desirable — held
      ],
    },
    {
      id: 'e13',
      firstName: 'Robert',
      lastName: 'King',
      jobId: 'j-safety-officer',
      location: 'Wellington Base',
      attributes: [
        { attributeDefinitionId: 'a-sms', proficiency: 'expert' },
        { attributeDefinitionId: 'a-human-factors', proficiency: 'expert' },
        { attributeDefinitionId: 'a-dg', proficiency: 'competent' },
        { attributeDefinitionId: 'a-avsec', proficiency: 'competent' },
        { attributeDefinitionId: 'a-crm', proficiency: 'competent' }, // desirable — held
      ],
    },
    {
      id: 'e14',
      firstName: 'Mia',
      lastName: 'Tanaka',
      jobId: 'j-cabin-crew',
      location: 'Auckland Hub',
      attributes: [
        { attributeDefinitionId: 'a-crm', proficiency: 'competent' },
        { attributeDefinitionId: 'a-dg', proficiency: 'competent', expiresOn: '2026-12-09' },
        { attributeDefinitionId: 'a-first-aid', proficiency: 'competent' },
        { attributeDefinitionId: 'a-fire-smoke', proficiency: 'competent' },
        { attributeDefinitionId: 'a-avsec', proficiency: 'competent' },
        { attributeDefinitionId: 'a-pax-service', proficiency: 'competent' },
      ],
    },
  ],
  // Regulation tasks — named bundles of competencies. Met/gap employees are computed from holdings.
  tasks: [
    {
      id: 't-preflight-safety',
      name: 'Pre-Flight Safety Checks',
      competencyIds: ['a-crm', 'a-fire-smoke', 'a-first-aid'],
    },
    {
      id: 't-dangerous-goods',
      name: 'Dangerous Goods Handling',
      competencyIds: ['a-dg', 'a-ramp-safety'],
    },
    {
      id: 't-safety-management',
      name: 'Safety Management Compliance',
      competencyIds: ['a-sms', 'a-human-factors'],
    },
  ],
  // Training courses that deliver competencies. "Completed" = the employee holds them (computed).
  trainings: [
    {
      id: 'tr-a320-conversion',
      name: 'A320 Type Conversion',
      integrationAbbreviation: 'LU',
      competencyIds: ['a-type-a320', 'a-crm'],
    },
    {
      id: 'tr-dg-recurrent',
      name: 'Dangerous Goods Recurrent',
      integrationAbbreviation: 'LU',
      competencyIds: ['a-dg'],
    },
    {
      id: 'tr-sms-fundamentals',
      name: 'Safety Management Fundamentals',
      integrationAbbreviation: 'LU',
      competencyIds: ['a-sms', 'a-human-factors'],
    },
    {
      id: 'tr-cabin-safety',
      name: 'Cabin Safety & Emergency Procedures',
      integrationAbbreviation: 'LU',
      competencyIds: ['a-fire-smoke', 'a-first-aid', 'a-avsec'],
    },
  ],
  // A group = a named subset of employees. "Auckland Hub Team" gathers the Auckland-based staff,
  // so the My Groups page shows a realistic subset rather than the whole company.
  groups: [
    {
      groupId: 'g-auckland',
      name: 'Auckland Hub Team',
      description: 'Crew and ground staff based at the Auckland hub.',
      memberEmployeeIds: ['e01', 'e03', 'e05', 'e08', 'e10', 'e12', 'e14'],
    },
  ],
};
